import React, { useState, useCallback, useEffect, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedView } from "@/components/ThemedView";
import {
  ThemedList,
  type ISortOption,
  type IGroupOption,
} from "@/components/ThemedList";
import { ThemedSegmentedControl } from "@/components/ThemedSegmentedControl";
import { useMessaging } from "@/hooks/useMessaging";
import { useAuth } from "@components/auth/useAuth";
import { MessageCard } from "@/components/messaging/MessageCard";
import { ComposeMessageModal } from "@/components/messaging/ComposeMessageModal";
import { MessageDetailModal } from "@/components/messaging/MessageDetailModal";
import { ISelectedRecipient } from "@/components/messaging/RecipientSelector";
import { CollapsibleFilterSection } from "@/components/filter";
import { useFilterPersistence } from "@/hooks/useFilterPersistence";
import { filterMessages, computeFilteredCounts } from "@/services/unified-filter-service";
import { MessageModel, MessageType } from "common247";

/**
 * Valid folder types for message categorization.
 */
export type MessageFolder = "inbox" | "sent" | "trash";

/**
 * Sort options for Inbox messages.
 */
const INBOX_SORT_OPTIONS: ISortOption<MessageModel>[] = [
  {
    key: "createdAt",
    label: "Date",
    getValue: (m) => m.createdAt || 0,
  },
  {
    key: "sender",
    label: "Sender",
    getValue: (m) => m.senderDisplayName || "",
  },
  {
    key: "subject",
    label: "Subject",
    getValue: (m) => m.subject || "",
  },
  {
    key: "type",
    label: "Type",
    getValue: (m) => m.type || "general",
  },
  {
    key: "unread",
    label: "Unread Status",
    getValue: (m) => (m.isRead ? 1 : 0),
  },
];

/**
 * Sort options for Sent messages.
 */
const SENT_SORT_OPTIONS: ISortOption<MessageModel>[] = [
  {
    key: "createdAt",
    label: "Date",
    getValue: (m) => m.createdAt || 0,
  },
  {
    key: "recipient",
    label: "Recipient",
    getValue: (m) =>
      m.recipientDisplayName ||
      (m.recipients && m.recipients.length > 0
        ? m.recipients.map((r) => r.displayName || r.contactId).join(", ")
        : m.targetName || ""),
  },
  {
    key: "subject",
    label: "Subject",
    getValue: (m) => m.subject || "",
  },
  {
    key: "type",
    label: "Type",
    getValue: (m) => m.type || "general",
  },
];

/**
 * Grouping options for Inbox messages.
 */
const INBOX_GROUP_OPTIONS: IGroupOption<MessageModel>[] = [
  {
    key: "isRead",
    label: "Read Status",
    getGroupValue: (m) => (m.isRead ? "Read Messages" : "Unread Messages"),
  },
  {
    key: "type",
    label: "Message Type",
    getGroupValue: (m) => `${m.type} Messages`,
  },
  {
    key: "sender",
    label: "Sender",
    getGroupValue: (m) => m.senderDisplayName || "Unknown Sender",
  },
];

/**
 * Grouping options for Sent messages.
 */
const SENT_GROUP_OPTIONS: IGroupOption<MessageModel>[] = [
  {
    key: "recipients",
    label: "Recipient / Group",
    getGroupValue: (m) => {
      if (m.recipientDisplayName?.trim()) {
        return `Sent to: ${m.recipientDisplayName.trim()}`;
      }
      if (m.targetName?.trim()) {
        return `Sent to: ${m.targetName.trim()}`;
      }
      if (m.recipients && m.recipients.length > 0) {
        const isGroup = m.recipients.some((r) => Boolean(r.groupId));
        if (isGroup) {
          const groupName = m.recipients.find((r) => Boolean(r.groupName))?.groupName;
          return groupName ? `Sent to: ${groupName}` : `Sent to: Group (${m.recipients.length} members)`;
        }
        const names = m.recipients.map((r) => r.displayName?.trim()).filter(Boolean);
        if (names.length > 0) {
          return `Sent to: ${names.join(", ")}`;
        }
        return `Sent to: ${m.recipients.length} recipient${m.recipients.length === 1 ? "" : "s"}`;
      }
      return `Sent to: Recipient`;
    },
  },
  {
    key: "type",
    label: "Message Type",
    getGroupValue: (m) => `${m.type} Messages`,
  },
];

/**
 * Messages screen displaying internal real-time messages between users and groups.
 * Provides Inbox, Sent, and Trash folder selectors, sorting, grouping, responsive view modes,
 * and composer workflows.
 */
export default function MessagesScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const { id: paramMessageId, folder: paramFolder } = useLocalSearchParams<{
    id?: string;
    folder?: MessageFolder;
  }>();

  const [activeFolder, setActiveFolder] = useState<MessageFolder>(
    paramFolder === "sent" ? "sent" : paramFolder === "trash" ? "trash" : "inbox"
  );
  const [composeModalVisible, setComposeModalVisible] = useState<boolean>(false);
  const [selectedMessage, setSelectedMessage] = useState<MessageModel | null>(null);

  // Sync folder state when URL search param changes
  useEffect(() => {
    if (paramFolder && (paramFolder === "inbox" || paramFolder === "sent" || paramFolder === "trash")) {
      if (paramFolder !== activeFolder) {
        setActiveFolder(paramFolder);
      }
    }
  }, [paramFolder]);

  const handleFolderChange = useCallback(
    (folder: MessageFolder) => {
      setActiveFolder(folder);
      router.setParams({ folder });
    },
    [router]
  );

  // Direct reply prepopulation states
  const [replyRecipient, setReplyRecipient] = useState<ISelectedRecipient | null>(null);
  const [replySubject, setReplySubject] = useState<string | undefined>(undefined);
  const [replyType, setReplyType] = useState<MessageType | undefined>(undefined);

  const {
    messages,
    isLoading,
    refreshMessages,
    sendNewMessage,
    markAsRead,
    markAsUnread,
    moveToTrash,
    restoreFromTrash,
  } = useMessaging();

  // Re-fetch when screen gains focus
  useFocusEffect(
    useCallback(() => {
      refreshMessages();
    }, [refreshMessages])
  );

  // Helper to determine if message is trashed by current user
  const isTrashedByMe = useCallback(
    (m: MessageModel) => {
      if (m.isTrashed || (m as any).trashed) return true;
      if (!user) return false;
      const currentUids = [user.uid, user.userId, (user as any).authUid].filter(Boolean) as string[];
      if (Array.isArray(m.recipients)) {
        const myRec = m.recipients.find((r) => currentUids.includes(r.contactId));
        if (myRec && myRec.trashed !== undefined) {
          return myRec.trashed;
        }
      }
      return false;
    },
    [user]
  );

  // Helper to determine if current user is a recipient of message
  const isRecipientOf = useCallback(
    (m: MessageModel) => {
      if (!user) return false;
      if (typeof m.isRecipient === "boolean") return m.isRecipient;
      const currentUids = [user.uid, user.userId, (user as any).authUid].filter(Boolean) as string[];
      if (Array.isArray(m.recipients) && m.recipients.length > 0) {
        return m.recipients.some(
          (r) =>
            currentUids.includes(r.contactId) ||
            Boolean(r.groupId) ||
            (user.email && r.contactId?.toLowerCase() === user.email.toLowerCase())
        );
      }
      if (m.recipientUserId && currentUids.includes(m.recipientUserId)) return true;
      if (m.recipientId && currentUids.includes(m.recipientId)) return true;
      if (m.targetType === "group" || Boolean(m.groupId)) return true;
      return false;
    },
    [user]
  );

  // Helper to determine if current user sent the message
  const isSentByMe = useCallback(
    (m: MessageModel) => {
      if (!user) return false;
      if (typeof m.isSent === "boolean") return m.isSent;
      const currentUids = [user.uid, user.userId, (user as any).authUid].filter(Boolean) as string[];
      if (m.senderUserId && currentUids.includes(m.senderUserId)) return true;
      if (m.senderContactId && currentUids.includes(m.senderContactId)) return true;
      if (user.email && m.senderEmail && m.senderEmail.toLowerCase() === user.email.toLowerCase()) {
        return true;
      }
      return false;
    },
    [user]
  );

  // Partition messages into Inbox, Sent, and Trash
  const inboxMessages = useMemo(() => {
    if (!user) return messages;
    return messages
      .filter((m) => !isTrashedByMe(m))
      .filter((m) => !isSentByMe(m) || isRecipientOf(m))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [messages, user, isTrashedByMe, isSentByMe, isRecipientOf]);

  const sentMessages = useMemo(() => {
    if (!user) return [];
    return messages
      .filter((m) => isSentByMe(m) && !isTrashedByMe(m))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [messages, user, isTrashedByMe, isSentByMe]);

  const trashMessages = useMemo(() => {
    if (!user) return [];
    return messages
      .filter((m) => isTrashedByMe(m))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [messages, user, isTrashedByMe]);

  const displayedMessages =
    activeFolder === "inbox"
      ? inboxMessages
      : activeFolder === "sent"
      ? sentMessages
      : trashMessages;

  // Unified filter persistence
  const { filter, setFilter, resetFilter } = useFilterPersistence("messages");

  const effectiveMessages = useMemo(() => {
    return filterMessages(displayedMessages, filter);
  }, [displayedMessages, filter]);

  const filteredCounts = useMemo(() => {
    return computeFilteredCounts({ messages: displayedMessages }, filter, "messages");
  }, [displayedMessages, filter]);

  const inboxUnreadCount = useMemo(
    () => inboxMessages.filter((m) => !m.isRead).length,
    [inboxMessages]
  );

  const folderOptions = useMemo(
    () => [
      {
        label:
          inboxUnreadCount > 0
            ? `Inbox (${inboxMessages.length} • ${inboxUnreadCount} new)`
            : `Inbox (${inboxMessages.length})`,
        value: "inbox" as MessageFolder,
      },
      {
        label: `Sent (${sentMessages.length})`,
        value: "sent" as MessageFolder,
      },
      {
        label: `Trash (${trashMessages.length})`,
        value: "trash" as MessageFolder,
      },
    ],
    [inboxMessages.length, inboxUnreadCount, sentMessages.length, trashMessages.length]
  );

  // Auto-navigate to specific message route when navigated to via URL (/messages?id=<messageId>)
  useEffect(() => {
    if (paramMessageId) {
      router.push(`/(tabs)/messages/${paramMessageId}` as any);
    }
  }, [paramMessageId, router]);

  /**
   * Opens compose modal in clean new message mode.
   */
  const handleOpenCompose = useCallback(() => {
    setReplyRecipient(null);
    setReplySubject(undefined);
    setReplyType(undefined);
    setComposeModalVisible(true);
  }, []);

  /**
   * Prepopulates composer for a direct response to a message.
   */
  const handleReply = useCallback((msg: MessageModel) => {
    const replyContactId = msg.senderContactId || msg.senderUserId;
    if (replyContactId) {
      setReplyRecipient({
        targetType: "contact",
        targetId: replyContactId,
        targetName: msg.senderDisplayName || "Sender",
        recipientUserId: msg.senderUserId,
        isShared: false,
        label: msg.senderDisplayName || "Sender",
      });
    } else {
      setReplyRecipient(null);
    }
    const cleanSubject = msg.subject ? msg.subject.trim() : "";
    setReplySubject(cleanSubject.startsWith("Re:") ? cleanSubject : `Re: ${cleanSubject}`);
    setReplyType(msg.type);
    setComposeModalVisible(true);
  }, []);

  /**
   * Handles replying directly from the MessageDetailModal.
   */
  const handleDetailReply = useCallback((msg: MessageModel) => {
    setSelectedMessage(null);
    handleReply(msg);
  }, [handleReply]);

  return (
    <ThemedScreen title="Messages" loading={isLoading && messages.length === 0} scrollable={false}>
      <ThemedView style={styles.container}>
        {/* Inbox / Sent Folder Selector */}
        <View style={styles.folderSelectorWrapper}>
          <ThemedSegmentedControl<MessageFolder>
            options={folderOptions}
            value={activeFolder}
            onChange={handleFolderChange}
            containerStyle={styles.folderSegmentedControl}
          />
        </View>

        {/* Unified Collapsible Entity Filter */}
        <CollapsibleFilterSection
          screen="messages"
          filter={filter}
          onFilterChange={setFilter}
          counts={filteredCounts}
          onReset={resetFilter}
        />

        <ThemedList<MessageModel>
          data={effectiveMessages}
          sortOptions={activeFolder === "sent" ? SENT_SORT_OPTIONS : INBOX_SORT_OPTIONS}
          defaultSortKey="createdAt"
          defaultSortDirection="desc"
          groupOptions={activeFolder === "sent" ? SENT_GROUP_OPTIONS : INBOX_GROUP_OPTIONS}
          persistenceKey={`messages_${activeFolder}`}
          filterKeys={
            activeFolder === "sent"
              ? ["subject", "body", "recipientDisplayName", "targetName", "recipientContactIds", "type"]
              : ["subject", "body", "senderDisplayName", "recipientContactIds", "type"]
          }
          filterPlaceholder={
            activeFolder === "inbox"
              ? "Search inbox by subject, sender, or content..."
              : activeFolder === "sent"
              ? "Search sent messages by subject, recipient, or content..."
              : "Search trash by subject, sender, or content..."
          }
          loading={isLoading}
          onRefresh={refreshMessages}
          refreshing={isLoading}
          addButton={{
            title: "+ New Message",
            onPress: handleOpenCompose,
            variant: "primary",
          }}
          emptyText={
            activeFolder === "inbox"
              ? "No inbox messages found."
              : activeFolder === "sent"
              ? "No sent messages found. Start a conversation by sending a new message."
              : "Trash folder is empty."
          }
          contentContainerStyle={styles.listContent}
          renderItem={(item, _index, isHovered, viewMode) => (
            <MessageCard
              key={item.id}
              message={item}
              viewMode={viewMode}
              isHovered={isHovered}
              folder={activeFolder}
              onPress={(msg) => router.push(`/(tabs)/messages/${msg.id}` as any)}
              onMarkRead={markAsRead}
              onMarkUnread={markAsUnread}
              onReply={(msg) => handleReply(msg)}
              onMoveToTrash={moveToTrash}
              onRestore={restoreFromTrash}
            />
          )}
        />

        {/* Compose Message Modal */}
        <ComposeMessageModal
          visible={composeModalVisible}
          onClose={() => setComposeModalVisible(false)}
          onSend={sendNewMessage}
          initialRecipient={replyRecipient}
          initialSubject={replySubject}
          initialType={replyType}
        />

        {/* Message Detail Modal */}
        <MessageDetailModal
          message={selectedMessage}
          visible={selectedMessage !== null}
          onClose={() => setSelectedMessage(null)}
          onMarkRead={markAsRead}
          onMarkUnread={markAsUnread}
          onReply={handleDetailReply}
          onMoveToTrash={moveToTrash}
          onRestore={restoreFromTrash}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    paddingBottom: 0,
    minHeight: 0,
  },
  folderSelectorWrapper: {
    marginBottom: 8,
    paddingHorizontal: 2,
    alignItems: "flex-start",
  },
  folderSegmentedControl: {
    maxWidth: 520,
    width: "100%",
  },
  listContent: {
    paddingBottom: 24,
  },
});


